<template>
  <div class="download-stats">
    <div class="page-header">
      <h2>软件下载量趋势</h2>
      <div class="header-controls">
        <div class="date-search">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :shortcuts="dateShortcuts"
          />
          <el-button type="primary" :icon="Search" @click="handleSearch">搜索</el-button>
        </div>
        <el-radio-group v-model="period" @change="handlePeriodChange">
          <el-radio-button label="day">日</el-radio-button>
          <el-radio-button label="week">周</el-radio-button>
          <el-radio-button label="month">月</el-radio-button>
        </el-radio-group>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-cards">
      <el-row :gutter="20">
        <el-col :span="8">
          <el-card class="stat-card">
            <template #header>
              <div class="card-header">
                <span>总下载量</span>
                <el-icon><Download /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ stats.totalDownloads }}</div>
              <div class="stat-label">累计下载次数</div>
            </div>
          </el-card>
        </el-col>
        
        <el-col :span="8">
          <el-card class="stat-card">
            <template #header>
              <div class="card-header">
                <span>选定日期下载量</span>
                <el-icon><DataLine /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ stats.periodDownloads }}</div>
              <div class="stat-label">选定时间段下载次数</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="8">
          <el-card class="stat-card">
            <template #header>
              <div class="card-header">
                <span>今日下载</span>
                <el-icon><Calendar /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ stats.todayDownloads }}</div>
              <div class="stat-label">今日下载次数</div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 下载趋势图表 -->
    <div class="chart-container">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-card class="chart-card">
            <template #header>
              <div class="card-header">
                <span>累计下载趋势</span>
              </div>
            </template>
            <div class="chart-content">
              <ChartBox
                :chart-data="cumulativeChartData"
                :chart-options="cumulativeChartOptions"
                height="400px"
              />
            </div>
          </el-card>
        </el-col>
        <el-col :span="12">
      <el-card class="chart-card">
        <template #header>
          <div class="card-header">
                <span>每日下载趋势</span>
          </div>
        </template>
        <div class="chart-content">
          <ChartBox
            :chart-data="downloadChartData"
            :chart-options="downloadChartOptions"
            height="400px"
          />
        </div>
      </el-card>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import ChartBox from '@/components/admin/ChartBox.vue'
import { Download, Calendar, DataLine, Search } from '@element-plus/icons-vue'
import request from '@/utils/request'

// 时间周期选择
const period = ref('day')

// 统计数据
const stats = ref({
  totalDownloads: 0,
  todayDownloads: 0,
  periodDownloads: 0
})

// 图表数据
const downloadChartData = ref({
  labels: [],
  datasets: [
    {
      label: '下载量',
      data: [],
      backgroundColor: 'rgba(64, 158, 255, 0.2)',
      borderColor: 'rgba(64, 158, 255, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }
  ]
})

const downloadChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          return `下载量: ${context.raw}`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        precision: 0
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  }
}

// 累计下载图表数据
const cumulativeChartData = ref({
  labels: [],
  datasets: [
    {
      label: '累计下载量',
      data: [],
      backgroundColor: 'rgba(100, 149, 237, 0.2)',
      borderColor: 'rgba(100, 149, 237, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }
  ]
})

const cumulativeChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          return `累计下载量: ${context.raw}`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        precision: 0
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  }
}

// 日期范围选择
const dateRange = ref([])
const dateShortcuts = [
  {
    text: '最近一周',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
      return [start, end]
    }
  },
  {
    text: '最近一个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
      return [start, end]
    }
  },
  {
    text: '最近三个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
      return [start, end]
    }
  }
]

// 更新图表数据
const updateChartData = (trendData) => {
  console.log('更新图表数据：', trendData)
  if (!trendData || trendData.length === 0) {
    // 如果没有数据，清空图表
    downloadChartData.value = {
      labels: [],
      datasets: [
        {
          label: '下载量',
          data: [],
          backgroundColor: 'rgba(64, 158, 255, 0.2)',
          borderColor: 'rgba(64, 158, 255, 1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }
      ]
    }
    cumulativeChartData.value = {
      labels: [],
      datasets: [
        {
          label: '累计下载量',
          data: [],
          backgroundColor: 'rgba(100, 149, 237, 0.2)',
          borderColor: 'rgba(100, 149, 237, 1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }
      ]
    }
    return
  }

  // 根据周期格式化日期标签
  const formatDate = (periodStr) => {
    switch (period.value) {
      case 'day':
        const date = new Date(periodStr)
        return date.toLocaleDateString()
      case 'week':
        return `第${periodStr.split('-')[1]}周`
      case 'month':
        const [year, month] = periodStr.split('-')
        return `${year}-${month}`
      default:
        return periodStr
    }
  }

  // 直接使用后端返回的完整趋势数据
  const chartLabels = trendData.map(item => formatDate(item.period))
  const chartData = trendData.map(item => item.count)

  // 计算累计下载量
  const cumulativeData = []
  let cumulative = 0
  chartData.forEach(count => {
    cumulative += count
    cumulativeData.push(cumulative)
  })

  // 更新每日下载趋势图表数据
  downloadChartData.value = {
    labels: chartLabels,
    datasets: [{
      label: '下载量',
      data: chartData,
      backgroundColor: 'rgba(64, 158, 255, 0.2)',
      borderColor: 'rgba(64, 158, 255, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }]
  }

  // 更新累计下载趋势图表数据
  cumulativeChartData.value = {
    labels: chartLabels,
    datasets: [{
      label: '累计下载量',
      data: cumulativeData,
      backgroundColor: 'rgba(100, 149, 237, 0.2)',
      borderColor: 'rgba(100, 149, 237, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }]
  }

  // 更新图表配置
  const maxCount = Math.max(...chartData)
  downloadChartOptions.scales.y.ticks.stepSize = Math.ceil(maxCount / 5)
  
  const maxCumulative = Math.max(...cumulativeData)
  cumulativeChartOptions.scales.y.ticks.stepSize = Math.ceil(maxCumulative / 5)
  
  console.log('图表数据更新完成，标签:', chartLabels)
  console.log('图表数据更新完成，每日数据:', chartData)
  console.log('图表数据更新完成，累计数据:', cumulativeData)
}

// 获取统计数据
const fetchStats = async () => {
  try {
    console.log('开始获取下载统计数据...')
    console.log('日期范围:', dateRange.value)
    console.log('开始日期:', dateRange.value[0]?.toISOString().split('T')[0])
    console.log('结束日期:', dateRange.value[1]?.toISOString().split('T')[0])
    
    const params = { 
        period: period.value,
      startDate: dateRange.value[0]?.toISOString().split('T')[0],
      endDate: dateRange.value[1]?.toISOString().split('T')[0]
      }
    console.log('请求参数:', params)
    
    const response = await request.get('/admin/stats/downloads', { params })
    console.log('获取到的数据：', response)
    
    if (!response || !response.trend) {
      throw new Error('返回数据格式不正确')
    }
    
    // 直接使用后端返回的统计数据
    stats.value = {
      totalDownloads: response.stats.totalDownloads,
      todayDownloads: response.stats.todayDownloads,
      periodDownloads: response.stats.periodDownloads
    }
    
    // 更新图表数据
    updateChartData(response.trend)
    console.log('统计数据更新完成：', stats.value)
  } catch (error) {
    console.error('获取统计数据失败：', error)
    ElMessage.error(error.message || '获取统计数据失败')
  }
}

// 初始化
onMounted(() => {
  // 设置默认日期范围为最近一个月
  dateRange.value = dateShortcuts[1].value()
  // 初始化时加载数据
  fetchStats()
})

// 周期变化处理
const handlePeriodChange = () => {
  if (!dateRange.value || dateRange.value.length !== 2) {
    ElMessage.warning('请先选择日期范围')
    return
  }
  fetchStats()
}

// 搜索处理
const handleSearch = () => {
  if (!dateRange.value || dateRange.value.length !== 2) {
    ElMessage.warning('请选择日期范围')
    return
  }
  fetchStats()
}
</script>

<style scoped>
.download-stats {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-controls {
  display: flex;
  gap: 16px;
  align-items: center;
}

.stats-cards {
  margin-bottom: 20px;
}

.stat-card {
  height: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-content {
  text-align: center;
  padding: 20px 0;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 8px;
}

.stat-label {
  color: #666;
  font-size: 14px;
}

.chart-container {
  margin-bottom: 20px;
}

.chart-content {
  height: 400px;
}

.date-search {
  display: flex;
  gap: 12px;
  align-items: center;
}
</style> 